import {
  HHBox,
  HHStack,
  HHTextField,
} from '@hinge-health/react-component-library';
import { Autocomplete, TextField } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { ReactNode, useState } from 'react';
import { FileProcessingStatus, IngestionFilters } from '../types';
import { DateRangePicker, DateRangePickerValue } from './date-range-picker';
import { StatusFilterChips } from './status-filter-chips';

interface FilterBarProps {
  filters: IngestionFilters;
  onFiltersChange: (filters: IngestionFilters) => void;
  dataProviders: string[];
  searchTerm: string;
  onSearchTermChange: (term: string) => void;
  /** Rendered after the controls, pushed to the far end of the row. */
  trailing?: ReactNode;
}

export const FILTER_BAR_TEST_ID = 'pdg-filter-bar';
export const PROVIDER_FILTER_AUTOCOMPLETE_TEST_ID =
  'pdg-provider-filter-autocomplete';
export const SEARCH_FILTER_INPUT_TEST_ID = 'pdg-search-filter-input';
export const DATE_RANGE_START_TEST_ID = 'pdg-date-range-start';
export const DATE_RANGE_END_TEST_ID = 'pdg-date-range-end';

export const FilterBar = ({
  filters,
  onFiltersChange,
  dataProviders,
  searchTerm,
  onSearchTermChange,
  trailing,
}: FilterBarProps): JSX.Element => {
  const [dateRange, setDateRange] = useState<
    [DateRangePickerValue, DateRangePickerValue]
  >([
    filters.createdAfter ? new Date(filters.createdAfter) : null,
    filters.createdBefore ? new Date(filters.createdBefore) : null,
  ]);

  const handleStatusesChange = (
    statuses: FileProcessingStatus[] | null,
  ): void => {
    onFiltersChange({ ...filters, statuses });
  };

  const handleProviderChange = (value: string | null): void => {
    onFiltersChange({ ...filters, dataProvider: value });
  };

  const handleDateRangeChange = (
    range: [DateRangePickerValue, DateRangePickerValue],
  ): void => {
    setDateRange(range);
    onFiltersChange({
      ...filters,
      createdAfter: range[0] ? range[0].toISOString() : null,
      createdBefore: range[1] ? range[1].toISOString() : null,
    });
  };

  return (
    <HHStack
      direction="row"
      gap={3}
      alignItems="center"
      flexWrap="wrap"
      width="100%"
      data-testid={FILTER_BAR_TEST_ID}
    >
      <StatusFilterChips
        statuses={filters.statuses}
        onChange={handleStatusesChange}
      />
      <Autocomplete
        data-testid={PROVIDER_FILTER_AUTOCOMPLETE_TEST_ID}
        options={dataProviders}
        value={filters.dataProvider}
        onChange={(_event, value): void => handleProviderChange(value)}
        size="small"
        sx={{ minWidth: 220 }}
        renderInput={(params): JSX.Element => (
          <TextField
            {...params}
            label="Data Provider"
            inputProps={{
              ...params.inputProps,
              role: 'combobox',
              name: 'dataProvider',
            }}
          />
        )}
      />
      <DateRangePickerWrapper
        dateRange={dateRange}
        onChange={handleDateRangeChange}
      />
      <HHTextField
        hhVariant="variant-bypass"
        variant="outlined"
        size="small"
        label="Search file name"
        data-testid={SEARCH_FILTER_INPUT_TEST_ID}
        value={searchTerm}
        onChange={(event): void => onSearchTermChange(event.target.value)}
        inputProps={{
          role: 'searchbox',
          name: 'fileNameSearch',
          'data-testid': `${SEARCH_FILTER_INPUT_TEST_ID}-native`,
        }}
      />
      {trailing && (
        <HHBox flex={1} display="flex" justifyContent="flex-end">
          {trailing}
        </HHBox>
      )}
    </HHStack>
  );
};

const DateRangePickerWrapper = ({
  dateRange,
  onChange,
}: {
  dateRange: [DateRangePickerValue, DateRangePickerValue];
  onChange: (range: [DateRangePickerValue, DateRangePickerValue]) => void;
}): JSX.Element => (
  // Self-wrap in a LocalizationProvider so the filter bar renders wherever
  // it is mounted without relying on an ancestor providing a date adapter —
  // this app configures LocalizationProvider per feature, not globally.
  <LocalizationProvider dateAdapter={AdapterDayjs}>
    <DateRangePicker
      dateRange={dateRange}
      setDateRange={(action): void => {
        const nextRange =
          typeof action === 'function' ? action(dateRange) : action;
        onChange(nextRange);
      }}
      startDateTestId={DATE_RANGE_START_TEST_ID}
      endDateTestId={DATE_RANGE_END_TEST_ID}
    />
  </LocalizationProvider>
);
